/**
 * Team access — who may read and who may change a team (#2060, master
 * account slice B3).
 *
 * `GET /api/teams/{id}` reports the caller's `role` on the team: `lead`,
 * `co_lead`, `member`, or — for an account on the platform's admin
 * allowlist that holds no membership on the team — `observer` (#2059).
 * An observer reads what a lead reads, and every write is refused by the
 * API with 403 "Read-only observer".
 *
 * The SPA mirrors that in two pieces:
 *
 *   - **Role helpers.** `hasLeadView(role)` decides which view a page
 *     renders (lead, co-lead and observer all get the lead's view);
 *     `canLeadWrite(role)` decides whether that view carries its write
 *     controls (lead and co-lead only).
 *   - **`<ReadOnlyProvider/>` + `useReadOnly()`.** The shell (and the team
 *     pages themselves) mark an observed team's subtree read-only, so a
 *     control deep in a page — a toolbar's Solve, a cell's menu — hides
 *     itself without the page threading a prop down to it. Providers nest
 *     with OR: once a parent says read-only, a child cannot undo it.
 */
import { type ReactNode, createContext, useContext } from "react";

/** The caller's role on a team, as the API reports it. */
export type TeamRoleName = "lead" | "co_lead" | "member" | "observer";

/** The account is reading this team through the admin allowlist. */
export function isObserverRole(role: string | null | undefined): boolean {
  return role === "observer";
}

/** The role may change the team the way a lead does. */
export function canLeadWrite(role: string | null | undefined): boolean {
  return role === "lead" || role === "co_lead";
}

/** The role sees the lead's view of the team (writes or not). */
export function hasLeadView(role: string | null | undefined): boolean {
  return canLeadWrite(role) || isObserverRole(role);
}

const ReadOnlyContext = createContext(false);

/** Marks a subtree read-only. Nested providers combine with OR. */
export function ReadOnlyProvider({
  readOnly,
  children,
}: {
  readOnly: boolean;
  children: ReactNode;
}) {
  const parent = useContext(ReadOnlyContext);
  return <ReadOnlyContext.Provider value={parent || readOnly}>{children}</ReadOnlyContext.Provider>;
}

/** True inside an observed team's pages: write controls hide themselves. */
export function useReadOnly(): boolean {
  return useContext(ReadOnlyContext);
}

/** The read-only chip's words, shared by the top bar and the tests. */
export const OBSERVER_CHIP_LABEL = "Viewing as Tarii · read only";
